///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useSelector } from "react-redux";
import { add, addDays, isPast, parseISO, subDays } from "date-fns";

import CustomDatePicker from "../form-controls/CustomDatePicker";
import messages from "../../helpers/messages";
import AutoComplete from "../form-controls/AutoComplete";
import { division, mode, strategyDurationOptions, bidScacType } from "../../helpers/types";
import getStrategyDuration from "../../helpers/getStrategyDuration";

function Timeline({
  bid: {
    totalRounds,
    openDateUTC,
    closeDateUTC,
    additionalBidRounds,
    startDateUTC,
    endDateUTC,
    awardDateUTC,
    strategicStartDateUTC,
    strategicEndDateUTC,
    strategyDuration,
    bidScac,
    modes
  },
  setBidState,
  validationData,
  removeError,
  userOrBidDivision
}) {
    const { useStrategicCostData, crmEnableImdlUi } = useSelector(state => state.featureFlags);
  const isImdlBid = modes?.some(m => m.mode === mode.imdl);
  const showImdlCrmNames = isImdlBid && bidScac?.value === bidScacType[1].value && crmEnableImdlUi;
  const isRxoNonImdlBid = bidScac?.value === bidScacType[1].value && !isImdlBid;
  const showCrmLabels = showImdlCrmNames || isRxoNonImdlBid;

  const handleStrategyDurationChange = option => {
    let strategicStartDate;

    if (strategicStartDateUTC) {
      strategicStartDate = parseISO(strategicStartDateUTC);
    } else {
      if (isPast(parseISO(startDateUTC))) {
        strategicStartDate = today;
      } else {
        strategicStartDate = parseISO(startDateUTC);
      }
    }

    setBidState("strategicStartDateUTC", strategicStartDate.toISOString());
    setBidState(
      "strategicEndDateUTC",
      add(strategicStartDate, {
        months: option.months
      }).toISOString()
    );
    setBidState("strategyDuration", option);
  };

  const handleAdditionalRoundChange = (index, date) => {
    const displayed = ensureAdditionalRoundsArray();
    const updatedRounds = [...displayed];
    updatedRounds[index] = {
      roundNumber: index + 2,
      dueDateUTC: date || null
    };
    setBidState("additionalBidRounds", updatedRounds);
  };

  const getMinDateForRound = () => closeDateUTC && new Date(closeDateUTC);

  const getMinDateForEffectiveStart = () => {
    if (additionalBidRounds?.length) {
      const lastRound = additionalBidRounds.reduce((latest, r) =>
        (!latest || r?.roundNumber > latest.roundNumber ? r : latest),
        null
      );
      if (lastRound?.dueDateUTC) return new Date(lastRound.dueDateUTC);
    }
    return closeDateUTC && new Date(closeDateUTC);
  };

  const ensureAdditionalRoundsArray = () => {
    const totalRoundsValue = typeof totalRounds === "object" ? totalRounds?.value : parseInt(totalRounds);
    const expectedAdditionalRounds = totalRoundsValue > 1 ? totalRoundsValue - 1 : 0;
    const currentRounds = additionalBidRounds || [];

    const newRounds = [];
    for (let i = 0; i < expectedAdditionalRounds; i++) {
      const roundNum = i + 2;
      const existingRound = currentRounds.find(r => r.roundNumber === roundNum);
      newRounds.push(existingRound || { roundNumber: roundNum, dueDateUTC: null });
    }
    return newRounds;
  };

  const today = new Date();
  const forecastMaxDate = add(today, {
    months: 18
  });

  const displayedAdditionalRounds = bidScac.value === bidScacType[1].value ? ensureAdditionalRoundsArray() : [];

  return (
    <div className="grid section-grid">
      <div className="grid__cell one-half pos-relative">
        <CustomDatePicker
          id="RFQ Open Date"
          value={openDateUTC}
          label={showCrmLabels ? messages.pricingLaunchDate.defaultMessage : messages.rfqOpenDate.defaultMessage}
          maxDate={closeDateUTC && new Date(closeDateUTC)}
          onChange={date => setBidState("openDateUTC", date)}
          onFocus={() =>
            !validationData.openDateUTC.isValid && removeError("openDateUTC")
          }
          error={!validationData.openDateUTC.isValid}
          validationMessage={validationData.openDateUTC.validationMessage}
        />
      </div>
      <div className="grid__cell one-half pos-relative">
        <CustomDatePicker
          id="RFQ Close Date"
          value={closeDateUTC}
          label={showCrmLabels ? messages.pricingDueDateWithoutDate.defaultMessage : messages.rfqCloseDateWithoutDate.defaultMessage}
          minDate={openDateUTC && new Date(openDateUTC)}
          maxDate={startDateUTC && new Date(startDateUTC)}
          onChange={date => setBidState("closeDateUTC", date)}
          onFocus={() =>
            !validationData.closeDateUTC.isValid && removeError("closeDateUTC")
          }
          error={!validationData.closeDateUTC.isValid}
          validationMessage={validationData.closeDateUTC.validationMessage}
        />
      </div>
      {displayedAdditionalRounds.map((round, index) => {
        const roundNumber = index + 2;
        const validation = validationData.additionalBidRounds?.[index] || { isValid: true, validationMessage: "" };
        return (
          <div key={`round-${roundNumber}`} className="grid__cell one-half pos-relative">
            <CustomDatePicker
              id={`Round ${roundNumber} Close Date`}
              value={round.dueDateUTC}
              label={
                isRxoNonImdlBid
                  ? messages[`round${roundNumber}DueDate`]?.defaultMessage || `Round ${roundNumber} Due Date`
                  : messages[`round${roundNumber}CloseDate`]?.defaultMessage || `Round ${roundNumber} Close Date`
              }
              minDate={getMinDateForRound()}
              maxDate={startDateUTC && new Date(startDateUTC)}
              onChange={date => handleAdditionalRoundChange(index, date)}
              onFocus={() =>
                !validation.isValid && removeError("additionalBidRounds")
              }
              error={!validation.isValid}
              validationMessage={validation.validationMessage}
            />
          </div>
        );
      })}
      <div className="grid__cell one-half pos-relative">
        <CustomDatePicker
          id="Effective Start Date"
          value={startDateUTC}
          label={
            useStrategicCostData
              ? (showCrmLabels ? messages.rateEffectiveDate.defaultMessage : messages.contractEffectiveStartDate.defaultMessage)
              : messages.effectiveStartDate.defaultMessage
          }
          minDate={getMinDateForEffectiveStart()}
          maxDate={endDateUTC && subDays(new Date(endDateUTC), 1)}
          onChange={date => setBidState("startDateUTC", date)}
          onFocus={() =>
            !validationData.startDateUTC.isValid && removeError("startDateUTC")
          }
          error={!validationData.startDateUTC.isValid}
          validationMessage={validationData.startDateUTC.validationMessage}
        />
      </div>
      <div className="grid__cell one-half pos-relative">
        <CustomDatePicker
          id="Effective End Date"
          value={endDateUTC}
          label={
            useStrategicCostData
              ? (showCrmLabels ? messages.rateExpirationDate.defaultMessage : messages.contractEffectiveEndDate.defaultMessage)
              : messages.effectiveEndDate.defaultMessage
          }
          minDate={startDateUTC && addDays(new Date(startDateUTC), 1)}
          onChange={date => setBidState("endDateUTC", date)}
          onFocus={() =>
            !validationData.endDateUTC.isValid && removeError("endDateUTC")
          }
          error={!validationData.endDateUTC.isValid}
          validationMessage={validationData.endDateUTC.validationMessage}
        />
      </div>
      {useStrategicCostData && userOrBidDivision === division.coy ? (
        <>
          <div
            className="grid__cell one-third pos-relative"
            data-testid="strategy-duration-container"
            id="strategy-duration"
          >
            <AutoComplete
              value={strategyDuration}
              label={messages.strategyDurationOptional.defaultMessage}
              staticOptions={strategyDurationOptions}
              handleChange={handleStrategyDurationChange}
            />
          </div>
          <div
            className="grid__cell one-third pos-relative"
            id="strategic-effective-start-date"
          >
            <CustomDatePicker
              id="Strategic Effective Start Date"
              value={strategicStartDateUTC}
              label={messages.strategicEffectiveStartDate.defaultMessage}
              minDate={today}
              maxDate={
                strategicEndDateUTC
                  ? parseISO(strategicEndDateUTC)
                  : forecastMaxDate
              }
              onChange={date => {
                setBidState("strategicStartDateUTC", date);
                setBidState(
                  "strategyDuration",
                  getStrategyDuration(date, strategicEndDateUTC)
                );
              }}
            />
          </div>
          <div
            className="grid__cell one-third pos-relative"
            id="strategic-effective-end-date"
          >
            <CustomDatePicker
              id="Strategic Effective End Date"
              value={strategicEndDateUTC}
              label={messages.strategicEffectiveEndDate.defaultMessage}
              minDate={
                strategicStartDateUTC ? parseISO(strategicStartDateUTC) : today
              }
              maxDate={forecastMaxDate}
              onChange={date => {
                setBidState("strategicEndDateUTC", date);
                setBidState(
                  "strategyDuration",
                  getStrategyDuration(strategicStartDateUTC, date)
                );
              }}
            />
          </div>
        </>
      ) : null}
      <div className="grid__cell one-half pos-relative">
        <CustomDatePicker
          id="Award Date"
          value={awardDateUTC}
          label={messages.awardDateOptional.defaultMessage}
          onChange={date => setBidState("awardDateUTC", date)}
          minDate={closeDateUTC && new Date(closeDateUTC)}
          maxDate={
            isRxoNonImdlBid && startDateUTC
              ? new Date(startDateUTC)
              : undefined
          }
          onFocus={() =>
            !validationData.awardDateUTC?.isValid && removeError("awardDateUTC")
          }
          error={!validationData.awardDateUTC?.isValid}
          validationMessage={validationData.awardDateUTC?.validationMessage}
        />
      </div>
    </div>
  );
}

export default Timeline;
